import { Job, JobStatus } from '../job';
import { Vec, Vec3 } from '../vec';
import { PcvizJobSiteViews, PcvizProjectViews, PcvizSceneView } from './application';
import { CoreConfiguration, CoreConfigurationCategories } from './core-configuration';
import { PcvizFeature, PcvizLayer, PcvizProject, PcvizWorkspace, PcvizWorkspaceScan, PcvizWorkspaceStationElement } from './gis';
import { PcvizUploadConfigData } from './jobs';
import { PcvizSelectionObject } from './utils';
import { PcvizBaseWorkflow, PcvizScanWorkflow } from './workflow/workflow';

export interface PcvizColor {
    r: number;
    g: number;
    b: number;
}
export interface PcvizDBRegistry {
    controllers: Map<string, any>;
    getController(id: string): any;
    getInstance(): PcvizDBRegistry;
    registerController(name: string, controllerClass: any): void;
    unregisterAllControllers(): void;
    resetControllers(): void;
}

export interface PcvizDBUtils {
    hexToRgb(color: string): PcvizColor;
    RgbTofullhex(color: number[]): string;
    RGBIntToFullHex: (color: number) => string;
    generateRandomRGBIntColor: () => number;
    generateUUID(): string;
    getLookAtMat4(eye: any, center: any, up: any, matrix: any, distance: number): void;
    subVec3(dir: any, center: any, eye: any): void;
    addVec3(out: Vec3, vec: Vec3, vecSec: Vec3): Vec3;
    selectionComesFromMe(selectionObject: PcvizSelectionObject, element: any): boolean;
    cleanUpFileNameCharacters(fileName: string): string;
    isEqualExtent(extent1: Vec, extent2: Vec): boolean;
    normalizeVec3(out: Vec3, vec: Vec3): Vec3;
    dotVec3(vec: Vec3, vecSec: Vec3): number;
    blobFromDataURL(dataURL: string): Blob;
    transposeMat4(out: any, mat: any): any;
    invertMat4(out: any, mat: any): any;

    TOP_VIEW: string;
    FRONT_VIEW: string;
    RIGHT_VIEW: string;
    BOTTOM_VIEW: string;
    BACK_VIEW: string;
    LEFT_VIEW: string;
    ISOMETRIC_VIEW: string;
    SELECTION_COLOR_KEY: string;
    BACKGROUND_COLOR_KEY: string;
    SECTION_BACKGROUND_COLOR_KEY: string;
    SECTION_GRID_COLOR_KEY: string;
    PICKING_CIRCLE_COLOR_KEY: string;
    NUMBER_OF_DECIMALS_KEY: string;
    GRID_ON_KEY: string;
    GRID_LABEL_ON_KEY: string;
    LAYERS_ORDER: string;
    PROJECTS_ORDER: string;
    PLANAR_IMAGE_DISTANCE_KEY: string;
    MOUSE_OR_TOUCH_KEY: string;
    REGIST_ALGO_VER_KEY: string;
    USER_INTERFACE_VER_KEY: string;
    BATTERY_SAVER: string;
    AUTOMATIC_LOD_KEY: string;
    DELETE_LAYERS_KEY: string;
    BACKGROUND_PROCESSING_KEY: string;
    VERITY_PAGE_SIZE: number;
    PAGE_SIZE: number;
    VERITY_AS_BUILT_KEY: string;
    VERITY_AS_DESIGN_KEY: string;
    VERITY_SMARTPOINTS_KEY: string;
    SCREENSHOT_WIDTH: number;
    SCREENSHOT_HEIGHT: number;
    LENGTH_ATTR: string;
    AREA_ATTR: string;
    DEFAULT_REPORT_PICK_POINT_COLOR: string;
}

export interface PcvizDBUnitController {
    _areaUnits: AreaUnit[];
    _distanceUnits: DistanceUnit[];
    _volumeUnits: VolumeUnit[];
    DISTANCE_TYPE: string;
    AREA_TYPE: string;
    VOLUME_TYPE: string;
    MILIMETERS: string;
    CENTIMETERS: string;
    METERS: string;
    KILOMETERS: string;
    INCHES: string;
    INTERNATIONAL_FEET: string;
    US_FEET: string;
    YARDS: string;
    MILES: string;

    registerListener(level: number, unit: string, fn: (obj: any) => any, id?: string): void;
    registerUnit(unit: string, type: string | Unit): void;
    getUnitDistance(id: string): DistanceUnit;
    getUnitArea(id: string): AreaUnit;
    getUnitVolume(id: string): VolumeUnit;
    convertToDistanceUnitWithoutLabel(value: number | undefined): number;
    convertToDistanceUnit(value: number): string;
    convertFromDistanceUnitWithoutLabel(value: number | undefined): number;
    getCurrentUnit(property: string): DistanceUnit | AreaUnit;
    removeListener(level: number, id: string): void;
    convertToUserUnit(measureValue: number, measureType: string): string;
    convertFromCustomUnit(value: number, unitId: any): number;
}

export enum CreateDownloadWorkspaceJsonMode {
    SELECT_MODE = 0,
    UNSELECT_MODE,
}

export interface PcvizWorkspaceController {
    _tmpWorkspace: PcvizWorkspace;

    getWorkspace(): PcvizWorkspace;
    uploadDownloadWorkspaceFile(workspaceDownloadProjectJson: any): any;
    uploadWorkspace(): Promise<void>;
    getWorkspaceDataCache(): any;
    initTempWorkspace(workflow: PcvizBaseWorkflow): void;
    stashPopAndApplyWorkspace(): Promise<void>;
    fetchWorkspace(project: PcvizProject, version?: number): Promise<void>;
    stashWorkspace(): void;
    emptyWorkspace(): void;
    getOrCreateWorkspace(project: PcvizProject, version: number): Promise<PcvizWorkspace>;
    createDownloadWorkspaceJson(
        workspace: PcvizWorkspace,
        scans: PcvizWorkspaceScan[],
        layersObjects?: any,
        unselectedObjects?: any,
        mode?: CreateDownloadWorkspaceJsonMode,
        options?: any
    ): any;
}

export interface PcvizDBListener {
    func: () => any;
    id?: string;
}

export type PcvizDBListenerArray = Array<PcvizDBListener>;

export interface PcvizDBListenerController {
    APPLICATION: number;
    PROJECT: number;
    getOrCreateListenerArray(key: any): PcvizDBListenerArray;
    getArrayFromListenerMap(): () => any[];
    registerListener(level: number, fn: (obj: any) => any, id?: string, key?: string): void;
    removeEventListeners(): void;
    removeListener(level: number, id: string): void;
    notify(...args: any[]): void;
    reset(): void;
}

export interface PcvizConfigurationController extends PcvizDBListenerController {
    new (): void;
    fileUrl: string;
    app: string;
    env: string;
    configuration: CoreConfiguration;
    configApp(app: string, env: string, fileUrl?: string): Promise<CoreConfiguration>;
    getConfig(): CoreConfiguration;
    isConfigLoaded(): boolean;
    getCategories(): CoreConfigurationCategories;
}

export interface PcvizDBSelectionController extends PcvizDBListenerController {
    createSelectionObject(type: string): PcvizSelectionObject;
    setSelectionObject(selectionObject: PcvizSelectionObject): void;
    getSelectionObject(): undefined | PcvizSelectionObject;
    getScannerStatus(): boolean;
    getScannerType(): string;
    setCurrentStation(station: string): void;
}

export type PcvizViews = PcvizProjectViews | PcvizJobSiteViews;

export interface PcvizDBViewController extends PcvizDBListenerController {
    addView(key: string, view: PcvizViews): void;
    changeViewPointAfterInitialize(options: any): void;
    zoomToSavedViewAfterInitialize(view: string): void;
    zoomToExtentAfterInitialize(envelope: PcvizFeature, options: any): void;
    zoomToLayerAfterInitialize(layer: PcvizLayer, options: any): void;
    zoomToFeatureAfterInitialize(layer: PcvizLayer, feature: PcvizFeature, options: any): void;
}

export interface PcvizUploadController {
    getJobStatus(id: number): JobStatus;
    jobExistsId(value: any): PcvizUploadConfigData;
    removeUploadJob(value: any): void;
    getJob(id: number): Job;
    initUploadJobsFromLocalStorage(): void;
}
export interface LinkedEdges {
    fixedStationUuid: string;
    movingStationUuid: string;
    transformationMatrix: number[];
    informationMatrix: number[];
    isOdometry: boolean;
    isPruned: boolean;
    edgeStatus: string;
    weight: number;
}
export interface EdgesObject {
    properties: {
        rootStationUuid: string;
    };
    edges: LinkedEdges[];
}

export interface PcvizC2CLinkController extends PcvizDBListenerController {
    setRefreshCallback(cb: any): void;
    init(workflow: PcvizScanWorkflow, view3d: PcvizSceneView): void;
    getLinkArray(): any[];
    saveLinks(): void;
    getParsedStationForLink(station: PcvizWorkspaceStationElement): any;
    updateLinkArray(): void;
    clearLinks(): void;
    drawLinks(): void;
    refreshLinkColors(): void;
    isRenderingLinks(): boolean;
    isInitiated(): boolean;
    isRenderingLinksSelected(): boolean;
    setSelectedBundle(bundle: any): void;
    getEdges(): EdgesObject;
}

export interface PcvizTargetColorController extends PcvizDBListenerController {
    init(workflow: PcvizScanWorkflow, view3d: PcvizSceneView): void;
    isInitiated(): void;
    updateAllTargetLayers(): void;
}

export interface PcvizDB {
    Registry: PcvizDBRegistry;
    Utils: PcvizDBUtils;
    UnitController: PcvizDBUnitController;
    ListenerController: PcvizDBListenerController;
    UploadController: any;
    ObjectController: any;
    LayerController: any;
    SelectionController: any;
    SymbolController: any;
    ViewController: any;
    UserPreferencesController: any;
    ApplicationController: any;
    MountController: any;
    BlobStorageController: any;
    ErrorController: any;
    ImageSelectionController: any;
    WorkspaceController: any;
    ModelLibraryController: any;
    ConfigurationController: any;
    OverwriteSymbologyController: any;
    ScanSelectionController: any;
    C2CLinkManager: PcvizC2CLinkController;
    PointVisibilityManagerController: any;
    PluginsController: any;
    OverwriteVisibilityController: any;
}

export type Unit = {
    id: string;
    label: string;
    fromMeters: number;
    text: string;
    precision: number;
};

export type AreaUnit = Unit;

export type VolumeUnit = Unit;

export type MapScale = 'metric' | 'us';
export type DistanceUnit = Unit & {
    mapScale: MapScale;
};

export interface PcvizUserPreferences extends Omit<PcvizDBListenerController, 'registerListener'> {
    getPreference(key: string): any;
    registerPreference(key: string, preference: any): void;
    registerListener(level: number, key: any, fn: (obj: any) => any, id: string): void;
}
